import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import AppChip from '@/components/base/AppChip';
import AppTab from '@/components/base/AppTab';
import SectionHeader from '@/components/base/SectionHeader';
import IconButton from '@/components/base/IconButton';
import MobileHeader from '@/components/base/MobileHeader';
import BottomActionBar from '@/components/feature/BottomActionBar';
import ShopNaverMap from '@/components/NaverMap/ShopNaverMap';
import PhotoDetailViewer from '@/pages/detail/components/PhotoDetailViewer';
import type { PhotoDetailImage } from '@/pages/detail/components/PhotoDetailViewer';
import FullReviewList from '@/pages/detail/components/FullReviewList';
import ReviewItem from '@/pages/detail/components/ReviewItem';
import ConsultationModal from '@/pages/detail/components/ConsultationModal';
import PlannerContentSection from '@/pages/detail/components/PlannerContentSection';
import {
  getPartnerShopDetail,
  type PartnerShopDetailPageData,
  type PartnerShopDetailInfoItem,
} from '@/api/partnerShopDetail';
import type {
  ConsultationShopData,
  DetailHeroData,
  DetailHomeData,
  DetailInfoData,
  DetailLookbookData,
  DetailReviewData,
  DetailSidebarData,
  DetailSummaryData,
  PartnerShopDetailViewData,
  ReviewPhotoItem,
  VendorPackage,
} from '@/pages/detail/types';
import styled from 'styled-components';
import faqList from './data/faqList';

type Tab = 'home' | 'lookbook' | 'review' | 'info';
type OverlayScreen = 'reviewList' | 'reviewPhotos' | null;

interface DetailOverlayState {
  detailOverlay?: boolean;
  photoDetailData?: {
    images: PhotoDetailImage[];
    initialIndex: number;
    title: string;
    subtitle?: string;
  };
}

const TAB_OPTIONS = [
  { key: 'home', label: '홈' },
  { key: 'lookbook', label: '룩북' },
  { key: 'review', label: '후기' },
  { key: 'info', label: '정보' },
];

function buildReviewPhotoCategories(photoItems: ReviewPhotoItem[]) {
  const map = new Map<string, number>();

  photoItems.forEach((photo) => {
    const category = photo.category || '기타';
    map.set(category, (map.get(category) || 0) + 1);
  });

  return [
    {
      label: '전체',
      count: photoItems.length,
    },
    ...Array.from(map.entries()).map(([label, count]) => ({
      label,
      count,
    })),
  ];
}

function getInfoValue(items: PartnerShopDetailInfoItem[], label: string, fallback = '상담 시 안내'): string {
  return items.find((item) => item.label === label)?.value || fallback;
}

function toCoordinate(value: number | string | null | undefined): number | undefined {
  const coordinate = Number(value);

  return Number.isFinite(coordinate) ? coordinate : undefined;
}

function normalizeReviewText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function normalizeNaverPlaceId(value: number | string | null | undefined): string | null {
  const placeId = String(value ?? '').trim();

  return placeId.length > 0 ? placeId : null;
}

function buildNaverMapUrl({ naverPlaceId, address }: Pick<DetailInfoData, 'naverPlaceId' | 'address'>): string {
  const placeId = normalizeNaverPlaceId(naverPlaceId);

  if (placeId) {
    return `https://map.naver.com/p/entry/place/${encodeURIComponent(placeId)}`;
  }

  return `https://map.naver.com/p/search/${encodeURIComponent(address)}`;
}

function mapDetailPageDataToViewData(pageData: PartnerShopDetailPageData, shopSlug: string): PartnerShopDetailViewData {
  const { shop } = pageData;
  const categories = shop.category || [];
  const enCategory = categories[0] === '예복' ? 'suit' : categories[0] === '한복' ? 'hanbok' : 'jewelry'
  const mobileHeroImage = pageData.heroImages?.mobile || '';
  const desktopHeroImage = pageData.heroImages?.desktop || '';
  const galleryImages = (pageData.heroImages?.gallery || []).filter(Boolean);
  const lookbookSections = (pageData.brandLookbookSections || [])
    .slice()
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((section) => {
      const photos = (section.photos || [])
        .slice()
        .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
        .map((photo) => photo.src)
        .filter(Boolean);

      return {
        sectionName: section.sectionName,
        description: section.description,
        photos,
      };
    })
    .filter((section) => section.photos.length > 0);
  const sectionLookbookImages = lookbookSections.flatMap((section) => section.photos);
  const lookbookImages = sectionLookbookImages.length > 0
    ? sectionLookbookImages
    : pageData.brandLookbook || [];
  const reviewPhotoItems = (pageData.reviewPhotos || []).map((photo, index) => ({
    id: String(photo.id ?? index),
    url: photo.src,
    author: photo.label || shop.name,
    category: photo.category || '후기',
  }));
  const plannerPoints = (pageData.verifiedReasonsJson?.items || []).map((item) => {
    return item.desc || item.title;
  });
  const reviewPros = (pageData.reviewSummary?.pros || [])
    .map((pro) => pro.description || pro.title)
    .filter(Boolean);
  const reviewNotes = (pageData.reviewSummary?.notes || [])
    .map((note) => note.description || note.title)
    .filter(Boolean);
  const reviewPhotoUrls = reviewPhotoItems.map((photo) => photo.url);
  const plannerContent = pageData.plannerContentJson || pageData.plannerContent || null;
  const plannerPhotos = pageData.plannerPhotosJson || pageData.plannerPhotos || [];
  const reviews = (pageData.reviews || []).map((review) => {
    const category = review.source || review.evaluationLevel || review.sentiment || review.tags?.[0] || '후기';

    return {
      id: review.id,
      title: review.title,
      source: review.source,
      author: review.writer?.nickname || '후기 작성자',
      writerId: review.writer?.id,
      writerNickname: review.writer?.nickname,
      writerProfileImageUrl: review.writer?.profileImageUrl,
      writerLevelName: review.writer?.memberLevelName,
      date: review.date,
      category,
      summary: normalizeReviewText(review.summary),
      content: normalizeReviewText(review.contentPreview || review.summary || review.title),
      reviewText: normalizeReviewText(review.reviewText || review.contentPreview || review.summary || review.title),
      tags: review.tags || [],
      goodPoints: review.goodPoints || [],
      checkPoints: review.checkPoints || [],
      originalUrl: review.originalUrl,
      evaluationLevel: review.evaluationLevel,
      sentiment: review.sentiment,
      confidence: review.confidence,
      photoUrls: review.photoThumbnails || [],
    };
  });
  const packages: VendorPackage[] = (pageData.products || []).map((product, index) => ({
    name: product.title,
    price: product.price,
    items: product.desc,
    isBest: index === 0,
  }));
  const address = shop.address || getInfoValue(pageData.information || [], '주소', pageData.address);
  const naverPlaceId = normalizeNaverPlaceId(shop.naverPlaceId);

  const reviewCount = shop.reviewCount || 0;
  const photoCount = shop.photoCount || reviewPhotoItems.length + lookbookImages.length;
  const description = shop.description || pageData.brandStory?.description || '';
  const reviewSummary = pageData.reviewSummary?.quote || pageData.verifiedReasonsJson?.introText || '';
  return {
    shopSlug,
    hero: {
      name: shop.name,
      location: shop.location,
      description,
      mobileImage: mobileHeroImage,
      desktopImage: desktopHeroImage,
      mainImage: desktopHeroImage || galleryImages[0] || lookbookImages[0] || reviewPhotoUrls[0] || '',
      images: [desktopHeroImage, ...galleryImages].filter(Boolean),
      lookbookImages,
      reviewCount,
      photoCount,
    },
    summary: {
      name: shop.name,
      location: shop.location,
      description,
      tags: categories,
      reviewCount,
      photoCount,
    },
    sidebar: {
      name: shop.name,
      reviewCount,
      photoCount,
    },
    home: {
      plannerContent: plannerContent
        ? {
          content: plannerContent,
          photos: plannerPhotos,
        }
        : null,
      plannerPoints,
      reviewSummary,
      reviewPhotoItems,
      lookbookImages,
    },
    lookbook: {
      lookbookSections: lookbookSections.length > 0
        ? lookbookSections
        : lookbookImages.length > 0
          ? [
            {
              sectionName: '브랜드 룩북',
              description: '업체가 직접 보여주는 공식 사진입니다.',
              photos: lookbookImages,
            },
          ]
          : [],
    },
    review: {
      reviewSummary,
      reviewPros,
      reviewNotes,
      reviewPhotoItems,
      reviews,
    },
    info: {
      name: shop.name,
      address,
      lat: toCoordinate(shop.lat),
      lon: toCoordinate(shop.lon),
      lng: toCoordinate(shop.lng),
      naverPlaceId,
      packages,
      hours: getInfoValue(pageData.information || [], '운영시간'),
      parking: getInfoValue(pageData.information || [], '주차'),
      reservationMethod: getInfoValue(pageData.information || [], '예약 방식'),
      consultationType: getInfoValue(pageData.information || [], '상담 방식'),
      amenities: (pageData.promotionBenefits || []).flatMap((benefitGroup) => {
        return benefitGroup.benefits.map((benefit) => benefit.title);
      }),
      faqs: faqList[enCategory],
    },
    consultationShop: {
      shopSlug,
      name: shop.name,
      category: categories[0] || '',
      location: shop.location,
    },
  };
}

/* ───── VendorHero ───── */
function VendorHero({ hero }: { hero: DetailHeroData }) {
  const navigate = useNavigate();
  const images = hero.images;
  const mobileImage = hero.mobileImage || hero.mainImage;

  const galleryImages = useMemo(() => {
    const result: string[] = [...images];
    const remaining = Math.max(0, 5 - result.length);
    if (hero.lookbookImages.length) {
      result.push(...hero.lookbookImages.slice(0, remaining));
    }
    while (result.length < 5) {
      result.push(result[result.length - 1] || result[0]);
    }
    return result.slice(0, 5);
  }, [images, hero.lookbookImages]);
  return (
    <>
      {/* ── Desktop Gallery ── */}
      <div className="hidden md:block relative max-w-[1180px] mx-auto px-10 pt-6">
        <div className="flex gap-2.5 h-[460px] rounded-2xl overflow-hidden">
          <div className="w-[52%] h-full rounded-2xl overflow-hidden">
            <img src={galleryImages[0]} alt={hero.name} className="w-full h-full object-cover" />
          </div>
          <div className="w-[48%] h-full grid grid-cols-2 grid-rows-2 gap-2.5">
            {galleryImages.slice(1, 5).map((img, i) => (
              <div key={i} className="rounded-2xl overflow-hidden">
                <img src={img} alt={`${hero.name} ${i + 2}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute top-8 left-12">
          <IconButton icon="ri-arrow-left-line" variant="translucent" aria-label="뒤로가기" onClick={() => navigate(-1)} />
        </div>
        {/* <div className="absolute top-8 right-12 flex items-center gap-2">
          <IconButton icon="ri-share-line" variant="translucent" aria-label="공유하기" />
        </div> */}
      </div>

      {/* ── Mobile Hero ── */}
      <div className="md:hidden relative w-full aspect-[4/3] max-h-[500px] overflow-hidden">
        <img
          src={mobileImage}
          alt={hero.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-static-black/20 via-static-black/25 to-static-black/60"></div>
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <IconButton icon="ri-arrow-left-line" variant="translucent" aria-label="뒤로가기" onClick={() => navigate(-1)} />
        </div>
        {/* <div className="absolute top-4 right-4 flex items-center gap-2">
          <IconButton icon="ri-share-line" variant="translucent" aria-label="공유하기" />
        </div> */}
        <div className="absolute bottom-4 left-4 right-4">
          {/* <span
            className="inline-block px-2.5 py-1 rounded-full bg-static-black/50 backdrop-blur-sm text-white text-caption font-medium mb-2"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
          >
            후기 기반 검증 업체
          </span> */}
          <h1
            className="text-title3 md:text-title1 text-white mb-1.5"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
          >
            {hero.name}
          </h1>
          {hero.description && (
            <p
              className="text-body2 leading-relaxed text-white mb-1.5 line-clamp-2"
              style={{ textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
            >
              {hero.description}
            </p>
          )}
          <p
            className="text-white text-label2"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
          >
            {hero.location} &middot; 실제 후기 {hero.reviewCount}개 &middot; 후기 사진 {hero.photoCount}장
          </p>
        </div>
      </div>
    </>
  );
}

/* ───── DesktopSidebar ───── */
function DesktopSidebar({ sidebar, onConsultationClick }: { sidebar: DetailSidebarData; onConsultationClick: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-[#E1DBD4] p-6">
      <div className="text-center mb-5">
        {/* <span className="inline-block px-2.5 py-1 rounded-full bg-[#F5F3F0] text-[#8A5A2B] text-caption font-medium mb-3">
          후기 기반 검증 업체
        </span> */}
        <h2 className="text-headline1 text-[#211C18] mb-2">{sidebar.name}</h2>
        <p className="text-label2 text-[#7A736C]">
          실제 후기 {sidebar.reviewCount}개 &middot; 후기 사진 {sidebar.photoCount}장
        </p>
      </div>
      <button
        onClick={onConsultationClick}
        className="block w-full text-center py-3.5 rounded-[22px] bg-[#B4563F] text-white text-label1 font-medium hover:bg-[#9F4633] transition-colors cursor-pointer whitespace-nowrap"
      >
        상담 문의하기
      </button>
    </div>
  );
}

/* ───── FilterPills ───── */
function FilterPills({
  categories,
  activeCategory,
  onSelect,
}: {
  categories: { label: string; count: number }[];
  activeCategory: string;
  onSelect: (label: string) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
      {categories.map((cat) => {
        const isActive = cat.label === activeCategory;
        return (
          <button
            key={cat.label}
            onClick={() => onSelect(cat.label)}
            className={`flex-shrink-0 px-3 md:px-3.5 h-[34px] md:h-[38px] rounded-full text-[13px] md:text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
              isActive ? 'bg-[#B4563F] text-static-white' : 'bg-static-white text-[#5F5953] border border-[#E1DBD4] hover:border-[#C8705A]'
            }`}
          >
            {cat.label} {cat.count}
          </button>
        );
      })}
    </div>
  );
}

/* ───── MasonryPhotoGrid ───── */
function MasonryPhotoGrid({ photos, onPhotoClick }: { photos: ReviewPhotoItem[]; onPhotoClick?: (index: number) => void }) {
  return (
    <div className="columns-2 gap-3">
      {photos.map((photo, i) => (
        <div
          key={photo.id}
          className="break-inside-avoid mb-3 rounded-2xl overflow-hidden relative cursor-pointer"
          onClick={() => onPhotoClick?.(i)}
        >
          <img src={photo.url} alt={`${photo.author} · ${photo.category}`} className="w-full h-auto object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-static-black/45 via-static-black/10 to-transparent rounded-2xl"></div>
          <div className="absolute bottom-3 left-3 right-3">
            <span className="text-static-white text-[12px] font-medium">{photo.author} · {photo.category}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───── PackageSection ───── */
function PackageSection({ packages }: { packages: VendorPackage[] }) {
  if (packages?.length === 0) {
    return null
  }
  return (
    <div>
      <SectionHeader title="예복 패키지" />
      <div className="text-body2 text-[#5F5953] mb-4">방문 전 참고할 수 있는 기본 패키지 구성과 가격대예요.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {packages.map((pkg, i) => (
          <div key={i} className={`rounded-xl border p-4 ${pkg.isBest ? 'border-[#C8705A] bg-[#FFF8F5]' : 'border-[#E1DBD4] bg-static-white'}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <h4 className="text-label1 font-semibold text-[#211C18]">{pkg.name}</h4>
                {pkg.isBest && <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-[#B4563F] text-static-white leading-none">BEST</span>}
              </div>
              <span className={`text-label1 font-semibold whitespace-nowrap ${pkg.isBest ? 'text-[#B4563F]' : 'text-[#211C18]'}`}>{pkg.price}</span>
            </div>
            <p className="text-body2 text-[#5F5953] leading-relaxed">{pkg.items}</p>
          </div>
        ))}
      </div>
      <p className="text-caption text-[#7A736C] mt-3 leading-relaxed">* 원단 등급·옵션에 따라 가격이 달라질 수 있어요. 정확한 견적은 상담 시 안내됩니다.</p>
    </div>
  );
}

/* ───── HomeTab ───── */
function HomeTab({
  home,
  onTabChange,
  onPhotoClick,
}: {
  home: DetailHomeData;
  onTabChange: (tab: Tab) => void;
  onPhotoClick: (items: ReviewPhotoItem[], index: number) => void;
}) {
  const [activePhotoFilter, setActivePhotoFilter] = useState('전체');
  const photoItems = useMemo(() => home.reviewPhotoItems || [], [home.reviewPhotoItems]);
  const photoCategories = useMemo(() => {
    return buildReviewPhotoCategories(photoItems);
  }, [photoItems]);
  const filteredPhotos = useMemo(() => {
    if (activePhotoFilter === '전체') return photoItems;
    return photoItems.filter((photo) => (photo.category || '기타') === activePhotoFilter);
  }, [photoItems, activePhotoFilter]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
      {home.plannerContent && (
        <PlannerContentSection
          content={home.plannerContent.content}
          photos={home.plannerContent.photos}
        />
      )}
      <div className='hidden'>
        <div className="bg-fill-alt rounded-xl px-5 py-5">
          <SectionHeader title="플래너의 추천 Point" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {home.plannerPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="font-serif text-lg text-accent-brown font-semibold leading-none mt-0.5 flex-shrink-0">{i + 1}</span>
                <p className="text-body2 text-label-neutral">{point}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <SectionHeader title="후기에서 자주 언급된 점" />
          <p className="text-body2 text-label-neutral leading-relaxed" dangerouslySetInnerHTML={{__html: home.reviewSummary}} />
        </div>
        <div>
          <SectionHeader title="후기 사진" trailing={
            <button onClick={() => onTabChange('review')} className="text-caption font-medium text-accent-brown hover:text-primary-normal transition-colors cursor-pointer whitespace-nowrap">
              후기 보기 <i className="ri-arrow-right-s-line ml-0.5 align-[-1px]"></i>
            </button>
          } />
          <p className="text-body2 text-[#5F5953] mb-3">실제 신랑님들이 남긴 사진을 종류별로 모았어요</p>
          <FilterPills categories={photoCategories} activeCategory={activePhotoFilter} onSelect={setActivePhotoFilter} />
          <div className="mt-4 grid grid-cols-3 gap-2 md:gap-1 md:grid-cols-4 lg:grid-cols-6">
            {filteredPhotos.slice(0, 30).map((photo, i) => (
              <ReviewPhoto
                key={photo.id}
                className="aspect-square rounded-[12px] overflow-hidden cursor-pointer relative group"
                onClick={() => onPhotoClick(filteredPhotos, i)}
              >
                <img src={photo.url} alt={photo.author ? `${photo.author} 후기 사진 ${i + 1}` : `후기 사진 ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-static-black/0 group-hover:bg-static-black/10 transition-colors duration-300 rounded-[12px]" />
              </ReviewPhoto>
            ))}
          </div>
        </div>
        <div className="pt-0">
          <SectionHeader title="브랜드 룩북" trailing={
            <button onClick={() => onTabChange('lookbook')} className="text-caption font-medium text-accent-brown hover:text-primary-normal transition-colors cursor-pointer whitespace-nowrap">
              룩북 보기 <i className="ri-arrow-right-s-line ml-0.5 align-[-1px]"></i>
            </button>
          } />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {home.lookbookImages.slice(0, 4).map((img, i) => (
              <div key={i} className="aspect-[3/4] rounded-[12px] overflow-hidden">
                <img src={img} alt={`룩북 ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── LookbookTab (section-based) ───── */
function LookbookTab({
  lookbook,
  onPhotoClick,
  onViewAll,
}: {
  lookbook: DetailLookbookData;
  onPhotoClick: (sectionIndex: number, photoIndex: number) => void;
  onViewAll: (sectionIndex: number) => void;
}) {
  const sections = lookbook.lookbookSections || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '44px' }}>
      {sections.map((section, si) => {
        return (
          <div key={si}>
            <div className="flex items-end justify-between mb-1.5">
              <h3 className="text-headline2 md:text-[22px] text-[#211C18]">{section.sectionName}</h3>
              <button onClick={() => onViewAll(si)} className="hidden lg:inline-flex items-center text-sm font-medium text-[#B4563F] hover:opacity-80 transition-colors cursor-pointer whitespace-nowrap">
                전체보기 <i className="ri-arrow-right-s-line ml-0.5"></i>
              </button>
            </div>
            <p className="text-body2 md:text-[15px] text-[#5F5953] mb-4">{section.description}</p>
            {/* Mobile (<768px): 2 cols, up to 6 photos */}
            <div className="md:hidden grid grid-cols-3 gap-3">
              {section.photos.slice(0, 6).map((url, pi) => (
                <div
                  key={pi}
                  className="aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => onPhotoClick(si, pi)}
                >
                  <img src={url} alt={`${section.sectionName} ${pi + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
            {/* Tablet (768-1023px): 3 cols, up to 6 photos */}
            <div className="hidden md:grid lg:hidden grid-cols-3 gap-3">
              {section.photos.slice(0, 6).map((url, pi) => (
                <div
                  key={pi}
                  className="aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => onPhotoClick(si, pi)}
                >
                  <img src={url} alt={`${section.sectionName} ${pi + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
            {/* Desktop compact (1024-1279px): 4 cols, compact */}
            <div className="hidden lg:grid xl:hidden grid-cols-4 gap-3">
              {section.photos.slice(0, 4).map((url, pi) => {
                const showOverlay = section.photos.length > 4 && pi === 3;
                const remaining = section.photos.length - 4;
                return (
                  <div
                    key={pi}
                    className="aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer relative"
                    onClick={() => onViewAll(si)}
                  >
                    <img src={url} alt={`${section.sectionName} ${pi + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    {showOverlay && (
                      <div className="absolute inset-0 bg-static-black/40 rounded-2xl flex items-center justify-center">
                        <span className="text-static-white text-[26px] font-bold">+{remaining}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Desktop wide (1280px+): 4 cols, spacious */}
            <div className="hidden xl:grid grid-cols-4 gap-4">
              {section.photos.slice(0, 4).map((url, pi) => {
                const showOverlay = section.photos.length > 4 && pi === 3;
                const remaining = section.photos.length - 4;
                return (
                  <div
                    key={pi}
                    className="aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer relative"
                    onClick={() => onViewAll(si)}
                  >
                    <img src={url} alt={`${section.sectionName} ${pi + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    {showOverlay && (
                      <div className="absolute inset-0 bg-static-black/40 rounded-2xl flex items-center justify-center">
                        <span className="text-static-white text-[26px] font-bold">+{remaining}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ReviewPhoto = styled.div`
  @media screen and (min-width: 1024px) {
    display: block !important;
    &:nth-child(n + 13) {
      display: none !important;
    }
  }
  
  @media screen and (min-width: 768px) and (max-width: 1023px) {
    display: block !important;
    &:nth-child(n+9) {
      display: none !important;
    }
  }

  @media screen and (max-width: 767px) {
    display: block !important;
    &:nth-child(n+10) {
      display: none !important;
    }
  }
`


/* ───── ReviewTab (summary-focused) ───── */
function ReviewTab({
  review,
  reviewCount,
  onOpenReviewList,
  onOpenPhotoGallery,
  onPhotoClick,
}: {
  review: DetailReviewData;
  reviewCount: number;
  onOpenReviewList: () => void;
  onOpenPhotoGallery: () => void;
  onPhotoClick: (items: ReviewPhotoItem[], index: number) => void;
}) {
  const [activePhotoFilter, setActivePhotoFilter] = useState('전체');
  const photoItems = useMemo(() => review.reviewPhotoItems || [], [review.reviewPhotoItems]);
  const photoCategories = useMemo(() => {
    return buildReviewPhotoCategories(photoItems);
  }, [photoItems]);
  const filteredPhotos = useMemo(() => {
    if (activePhotoFilter === '전체') return photoItems;
    return photoItems.filter((photo) => (photo.category || '기타') === activePhotoFilter);
  }, [photoItems, activePhotoFilter]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
      
      <div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F8F1EA] text-[#8A5A2B] text-caption font-medium mb-4">
          <i className="ri-pencil-line text-[#8A5A2B]"></i>실제 후기 요약
        </span>
        <p className="text-body2 text-label-neutral leading-relaxed md:max-w-[640px]" dangerouslySetInnerHTML={{__html: review.reviewSummary}}/>
      </div>

      {(review.reviewPros.length > 0 || review.reviewNotes.length > 0) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
        {review.reviewPros.length > 0 && (
        <div>
          <SectionHeader title="좋았던 점" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {review.reviewPros.map((good, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-1 h-1 rounded-full bg-accent-gold mt-2 flex-shrink-0"></span>
                <p className="text-body2 text-label-neutral">{good}</p>
              </div>
            ))}
          </div>
        </div>
        )}
        {review.reviewNotes.length > 0 && (
        <div>
          <SectionHeader title="방문 전 살펴볼 부분" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {review.reviewNotes.map((note, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-1 h-1 rounded-full bg-label-assistive mt-2 flex-shrink-0"></span>
                <p className="text-body2 text-label-neutral">{note}</p>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
      )}
      <div>
        <SectionHeader
          title="후기 사진"
          trailing={
            <button onClick={onOpenPhotoGallery} className="text-caption font-medium text-[#B4563F] hover:opacity-80 transition-colors cursor-pointer whitespace-nowrap">
              사진 전체보기 <i className="ri-arrow-right-s-line ml-0.5 align-[-1px]"></i>
            </button>
          }
        />
        <p className="text-body2 text-[#5F5953] mb-3">실제 신랑님들이 남긴 사진을 종류별로 모았어요.</p>
        <FilterPills categories={photoCategories} activeCategory={activePhotoFilter} onSelect={setActivePhotoFilter} />
        <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3 md:grid-cols-4 lg:grid-cols-6">
          {filteredPhotos.slice(0, 30).map((photo, i) => (
            <ReviewPhoto
              key={i}
              className="aspect-square rounded-[12px] overflow-hidden cursor-pointer relative group"
              onClick={() => onPhotoClick(filteredPhotos, i)}
            >
              <img src={photo.url} alt={photo.author ? `${photo.author} 후기 사진 ${i + 1}` : `후기 사진 ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-static-black/0 group-hover:bg-static-black/10 transition-colors duration-300 rounded-[12px]" />
            </ReviewPhoto>
          ))}
        </div>
        <button
          onClick={onOpenPhotoGallery}
          className="md:hidden w-full mt-5 py-3.5 rounded-[22px] border border-[#E1DBD4] text-[#3F3A35] text-label1 font-medium hover:bg-[#F5F3F0] transition-colors cursor-pointer whitespace-nowrap"
        >
          후기 사진 모두 보기 ({review.reviewPhotoItems.length}장)
        </button>
      </div>
      {review.reviews.length > 0 && (
        <div>
          <SectionHeader 
            title="대표 후기" 
             trailing={
              <button onClick={onOpenReviewList} className="text-caption font-medium text-[#B4563F] hover:opacity-80 transition-colors cursor-pointer whitespace-nowrap">
                후기 전체보기 <i className="ri-arrow-right-s-line ml-0.5 align-[-1px]"></i>
              </button>
            }  
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {review.reviews.slice(0, 3).map((reviewItem) => (
              <ReviewItem key={reviewItem.id} review={reviewItem} />
            ))}
          </div>
          {review.reviews.length > 0 && (
            <button
              onClick={onOpenReviewList}
              className="mt-5 md:hidden w-full py-3.5 rounded-[22px] border border-[#B4563F] text-[#B4563F] text-label1 font-medium hover:bg-[#FFF8F5] transition-colors cursor-pointer whitespace-nowrap"
            >
              전체 후기 보기 ({reviewCount}개)
            </button>
          )}
        </div>
      )}

      

      
    </div>
  );
}

/* ───── InfoTab ───── */
function InfoTab({ info }: { info: DetailInfoData }) {
  const naverMapUrl = buildNaverMapUrl(info);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
      <PackageSection packages={info.packages} />
      <div>
        <SectionHeader title="위치" />
        <div className="rounded-xl overflow-hidden border border-[#E1DBD4]">
          {info.lat !== undefined && (info.lng !== undefined || info.lon !== undefined) ? (
            <ShopNaverMap
              id="detail-shop-location"
              name={info.name}
              address={info.address}
              lat={info.lat}
              lng={info.lng}
              lon={info.lon}
              minHeight={300}
              zoomLevel={16}
              style={{ borderRadius: 0 }}
            />
          ) : (
            <div className="h-[200px] md:h-[300px] bg-[#F7F2EA] flex items-center justify-center px-4 text-center">
              <p className="text-body2 text-[#7A736C]">지도 좌표를 준비 중입니다.</p>
            </div>
          )}
        </div>
        <p className="text-[13px] md:text-[15px] text-[#3F3A35] mt-2">{info.address}</p>
        <a href={naverMapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-2 text-label1 font-medium text-[#B4563F] hover:opacity-80 transition-colors cursor-pointer whitespace-nowrap">
          길찾기<i className="ri-arrow-right-up-line ml-1"></i>
        </a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
        {[
          { icon: 'ri-time-line', label: '운영시간', value: info.hours },
          { icon: 'ri-parking-line', label: '주차', value: info.parking },
          { icon: 'ri-calendar-check-line', label: '예약 방식', value: info.reservationMethod },
          { icon: 'ri-user-voice-line', label: '상담 방식', value: info.consultationType },
        ].map((item, i) => (
          <div key={i} className="flex items-start py-3.5 gap-3 border-b border-[#E1DBD4] md:border-0">
            <i className={`${item.icon} text-[#7A736C] mt-0.5`}></i>
            <div>
              <p className="text-caption text-[#7A736C] mb-0.5">{item.label}</p>
              <p className="text-body2 md:text-[15px] text-[#3F3A35]">{item.value}</p>
            </div>
          </div>
        ))}
      </div>
      {
        info.amenities?.length > 0 && (
          <div className="pt-0 -mt-4">
            <div className="flex items-start py-3.5 gap-3">
              <i className="ri-check-double-line text-[#7A736C] mt-0.5"></i>
              <div>
                <p className="text-caption text-[#7A736C] mb-0.5">편의사항</p>
                <div className="flex flex-wrap gap-1.5">
                  {info.amenities.map((a) => (
                    <span key={a} className="px-2.5 py-0.5 rounded-full bg-[#F5F3F0] text-[#5F5953] text-caption">{a}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      }
      <div>
        <SectionHeader title="FAQ" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {info.faqs.map((faq, i) => (
            <details key={i} className="group border border-[#E1DBD4] rounded-xl">
              <summary className="flex items-center justify-between px-4 py-3.5 cursor-pointer list-none text-label1 md:text-[15px] font-medium text-[#211C18]">
                {faq.question}
                <i className="ri-arrow-down-s-line text-[#9A9188] group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-4 pb-3.5 text-body2 md:text-[15px] text-[#3F3A35] leading-relaxed">{faq.answer}</div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───── FullReviewPhotoView ───── */
function FullReviewPhotoView({
  shopName,
  photoItems,
  onClose,
  onPhotoClick,
}: {
  shopName: string;
  photoItems: ReviewPhotoItem[];
  onClose: () => void;
  onPhotoClick: (images: PhotoDetailImage[], index: number) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const PHOTO_PAGE_SIZE = 20;

  const categories = useMemo(() => {
    return buildReviewPhotoCategories(photoItems);
  }, [photoItems]);

  const [activeFilter, setActiveFilter] = useState('전체');
  const [visibleCount, setVisibleCount] = useState(PHOTO_PAGE_SIZE);

  const filteredPhotos = useMemo(() => {
    if (activeFilter === '전체') return photoItems;
    return photoItems.filter((photo) => (photo.category || '기타') === activeFilter);
  }, [photoItems, activeFilter]);

  const visiblePhotos = useMemo(() => {
    return filteredPhotos.slice(0, visibleCount);
  }, [filteredPhotos, visibleCount]);

  const hasMorePhotos = visibleCount < filteredPhotos.length;

  const detailImages = useMemo<PhotoDetailImage[]>(
    () => photoItems.map((p) => ({ id: p.id, url: p.url, label: `${p.author} · ${p.category}` })),
    [photoItems]
  );

  useEffect(() => {
    setVisibleCount(PHOTO_PAGE_SIZE);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [activeFilter, photoItems]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMorePhotos) return;

    const triggerPoint = el.scrollHeight * 0.7;
    if (el.scrollTop + el.clientHeight < triggerPoint) return;

    setVisibleCount((count) => Math.min(count + PHOTO_PAGE_SIZE, filteredPhotos.length));
  }, [filteredPhotos.length, hasMorePhotos]);

  const handlePhotoClick = useCallback(
    (filteredIndex: number) => {
      const clickedItem = filteredPhotos[filteredIndex];
      const globalIndex = photoItems.findIndex((p) => p.id === clickedItem.id);
      onPhotoClick(detailImages, globalIndex >= 0 ? globalIndex : 0);
    },
    [filteredPhotos, photoItems, detailImages, onPhotoClick]
  );

  return (
    <div
      ref={scrollContainerRef}
      className="fixed inset-0 z-50 bg-static-white overflow-y-auto"
      onScroll={handleScroll}
    >
      <div className="md:hidden">
        <MobileHeader onBack={onClose} title="후기 사진" rightContent={<>총 {photoItems.length}장</>}>
          <div className='pt-4'>
            <FilterPills categories={categories} activeCategory={activeFilter} onSelect={setActiveFilter} />
          </div>
        </MobileHeader>
      </div>
      {/* ── Desktop Header ── */}
      <div className="hidden md:block">
        <div className="max-w-[1120px] mx-auto px-8 pt-10 pb-6">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 text-sm text-[#5F5953] hover:text-[#B4563F] transition-colors cursor-pointer mb-6"
          >
            <i className="ri-arrow-left-line"></i> {shopName}으로 돌아가기
          </button>
          <h1 className="text-[28px] font-bold text-[#211C18] mb-2">후기 사진</h1>
          <p className="text-[15px] text-[#5F5953] mb-1">실제 신랑님들이 남긴 사진을 종류별로 모았어요.</p>
          <p className="text-sm text-[#7A736C] mb-5">총 {photoItems.length}장</p>
          <FilterPills categories={categories} activeCategory={activeFilter} onSelect={setActiveFilter} />
        </div>
      </div>
      <div className="px-4 pt-4 pb-24 md:px-8 md:max-w-[1120px] md:mx-auto">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-3">
          {visiblePhotos.map((photo, i) => (
            <FullReviewPhoto
              key={photo.id}
              className="break-inside-avoid mb-3 rounded-2xl overflow-hidden cursor-pointer relative"
              onClick={() => handlePhotoClick(i)}
            >
              <img src={photo.url} alt={`${photo.author} · ${photo.category}`} className="w-full h-auto object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-static-black/45 via-static-black/10 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-3 left-3 right-3">
                <span className="text-static-white text-[12px] font-medium">{photo.author} · {photo.category}</span>
              </div>
            </FullReviewPhoto>
          ))}
        </div>
        {!hasMorePhotos && filteredPhotos.length > 0 && (
          <div className="relative mt-8 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-line-divider"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-static-white px-5 text-label2 text-label-assistive">
                리스트의 마지막입니다
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
const FullReviewPhoto = styled.div`

`
/* ───── Page ───── */
export default function PartnerShopDetail() {
  const params = useParams<{ shopSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const shopSlug = params.shopSlug || '';

  const [pageData, setPageData] = useState<PartnerShopDetailPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [photoDetailData, setPhotoDetailData] = useState<{
    images: PhotoDetailImage[];
    initialIndex: number;
    title: string;
    subtitle?: string;
  } | null>(null);
  const [lookbookPhotoDetailData, setLookbookPhotoDetailData] = useState<{
    images: PhotoDetailImage[];
    initialIndex: number;
    title: string;
    subtitle?: string;
  } | null>(null);

  const [isCompactHeaderVisible, setIsCompactHeaderVisible] = useState(false);
  const [showConsultation, setShowConsultation] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const previousDetailPathRef = useRef<string | null>(null);

  const overlaySegments = useMemo(() => {
    if (!shopSlug) return [];
    const detailBasePath = `/detail/${shopSlug}`;
    const suffix = location.pathname.startsWith(detailBasePath)
      ? location.pathname.slice(detailBasePath.length)
      : '';

    return suffix.split('/').filter(Boolean);
  }, [location.pathname, shopSlug]);

  const overlayScreen = useMemo<OverlayScreen>(() => {
    if (overlaySegments[0] === 'reviews') return 'reviewList';
    if (overlaySegments[0] === 'review-photos') return 'reviewPhotos';
    return null;
  }, [overlaySegments]);

  const photoRouteImageId = useMemo(() => {
    if (overlaySegments[0] !== 'review-photos') return null;
    if (!overlaySegments[1]) return null;

    try {
      return decodeURIComponent(overlaySegments[1]);
    } catch {
      return overlaySegments[1];
    }
  }, [overlaySegments]);

  const isPhotoDetailRoute = photoRouteImageId !== null;
  const overlayState = location.state as DetailOverlayState | null;

  useEffect(() => {
    let ignore = false;

    async function loadDetail() {
      if (!shopSlug) {
        setErrorMessage('업체 주소가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const result = await getPartnerShopDetail(shopSlug, {
          preview: true,
        });

        if (!ignore) {
          setPageData(result);
        }
      } catch (error) {
        console.error('[PartnerShopDetail] failed to load partner shop detail', error);

        if (!ignore) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : '업체 상세 정보를 불러오지 못했습니다.'
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadDetail();

    return () => {
      ignore = true;
    };
  }, [shopSlug]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsCompactHeaderVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const detailData = useMemo(() => {
    if (!pageData || !shopSlug) return null;

    return mapDetailPageDataToViewData(pageData, shopSlug);
  }, [pageData, shopSlug]);

  const handlePhotoDetailOpen = useCallback(
    (images: PhotoDetailImage[], index: number, title: string, subtitle?: string) => {
      const nextPhotoDetailData = { images, initialIndex: index, title, subtitle };
      const imageId = images[index]?.id;
      if (!imageId) return;

      setPhotoDetailData(nextPhotoDetailData);
      navigate(`/detail/${shopSlug}/review-photos/${encodeURIComponent(imageId)}`, {
        state: {
          detailOverlay: true,
          photoDetailData: nextPhotoDetailData,
        } satisfies DetailOverlayState,
      });
    },
    [navigate, shopSlug]
  );

  const reviewPhotosToDetailImages = useCallback(
    (items: ReviewPhotoItem[]): PhotoDetailImage[] =>
      items.map((p) => ({ id: p.id, url: p.url, label: `${p.author} · ${p.category}` })),
    []
  );

  const handleReviewPhotoClick = useCallback(
    (items: ReviewPhotoItem[], index: number) => {
      handlePhotoDetailOpen(reviewPhotosToDetailImages(items), index, '후기 사진');
    },
    [handlePhotoDetailOpen, reviewPhotosToDetailImages]
  );

  const handleLookbookPhotoClick = useCallback(
    (sectionIndex: number, photoIndex: number) => {
      const section = detailData?.lookbook.lookbookSections?.[sectionIndex];
      if (!section || section.photos.length === 0) return;

      const images: PhotoDetailImage[] = section.photos.map((url, index) => ({
        id: `lookbook-${sectionIndex}-${index}`,
        url,
        label: `${section.sectionName} ${index + 1}`,
      }));

      setLookbookPhotoDetailData({
        images,
        initialIndex: Math.max(0, Math.min(photoIndex, images.length - 1)),
        title: section.sectionName,
        subtitle: section.description,
      });
    },
    [detailData?.lookbook.lookbookSections]
  );

  const handleLookbookViewAll = useCallback(
    (sectionIndex: number) => {
      handleLookbookPhotoClick(sectionIndex, 0);
    },
    [handleLookbookPhotoClick]
  );

  const handlePhotoDetailClose = useCallback(() => {
    setPhotoDetailData(null);
    if (overlayState?.detailOverlay) {
      navigate(-1);
      return;
    }

    navigate(`/detail/${shopSlug}/review-photos`, { replace: true });
  }, [navigate, overlayState?.detailOverlay, shopSlug]);

  const handleOverlayClose = useCallback(() => {
    if (overlayState?.detailOverlay) {
      navigate(-1);
      return;
    }

    navigate(`/detail/${shopSlug}`, { replace: true });
  }, [navigate, overlayState?.detailOverlay, shopSlug]);

  const handleOpenReviewList = useCallback(() => {
    navigate(`/detail/${shopSlug}/reviews`, {
      state: { detailOverlay: true } satisfies DetailOverlayState,
    });
  }, [navigate, shopSlug]);

  const handleOpenPhotoGallery = useCallback(() => {
    navigate(`/detail/${shopSlug}/review-photos`, {
      state: { detailOverlay: true } satisfies DetailOverlayState,
    });
  }, [navigate, shopSlug]);

  useEffect(() => {
    if (!isPhotoDetailRoute) {
      setPhotoDetailData(null);
    }
  }, [isPhotoDetailRoute]);

  useLayoutEffect(() => {
    if (!shopSlug) return;

    const detailBasePath = `/detail/${shopSlug}`;
    const previousPath = previousDetailPathRef.current;
    const isSameDetailFlow =
      previousPath === detailBasePath ||
      Boolean(previousPath?.startsWith(`${detailBasePath}/`));

    if (!isSameDetailFlow) {
      window.scrollTo({ top: 0, left: 0 });
    }

    previousDetailPathRef.current = location.pathname;
  }, [location.pathname, shopSlug]);

  const resolvedPhotoDetailData = useMemo(() => {
    if (!isPhotoDetailRoute) return null;
    if (photoDetailData) return photoDetailData;
    if (overlayState?.photoDetailData) return overlayState.photoDetailData;
    if (!detailData) return null;

    const images = reviewPhotosToDetailImages(detailData.review.reviewPhotoItems);
    if (!images.length) return null;

    const initialIndex = images.findIndex((image) => image.id === photoRouteImageId);
    if (initialIndex < 0) return null;

    return {
      images,
      initialIndex,
      title: '후기 사진',
    };
  }, [
    detailData,
    isPhotoDetailRoute,
    overlayState?.photoDetailData,
    photoDetailData,
    photoRouteImageId,
    reviewPhotosToDetailImages,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-normal flex items-center justify-center">
        <p className="text-body2 text-label-alt">업체 정보를 불러오는 중입니다.</p>
      </div>
    );
  }

  if (errorMessage || !detailData) {
    return (
      <div className="min-h-screen bg-bg-normal flex items-center justify-center">
        <div className="text-center">
          <p className="text-headline1 text-label-neutral mb-4">업체 정보를 불러오지 못했습니다</p>
          <p className="text-body2 text-label-alt mb-4">{errorMessage || '잠시 후 다시 시도해 주세요.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-primary-normal text-label1 font-medium hover:text-primary-strong transition-colors cursor-pointer"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const hasActiveTabContent = (tab: Tab) => {
    switch (tab) {
      case 'home':
        return <HomeTab home={detailData.home} onTabChange={setActiveTab} onPhotoClick={handleReviewPhotoClick} />;
      case 'lookbook': {
        return (
          <LookbookTab
            lookbook={detailData.lookbook}
            onPhotoClick={handleLookbookPhotoClick}
            onViewAll={handleLookbookViewAll}
          />
        );
      }
      case 'review':
        return (
          <ReviewTab
            reviewCount={detailData.summary.reviewCount}
            review={detailData.review}
            onOpenReviewList={handleOpenReviewList}
            onOpenPhotoGallery={handleOpenPhotoGallery}
            onPhotoClick={handleReviewPhotoClick}
          />
        );
      case 'info':
        return <InfoTab info={detailData.info} />;
    }
  };

  const isSubScreen = overlayScreen !== null || isPhotoDetailRoute;

  return (
    <div className="min-h-screen bg-bg-normal">
      <div className="hidden md:block"><Navbar /></div>

      {/* Full Review List */}
      {overlayScreen === 'reviewList' && (
        <FullReviewList
          shopSlug={detailData.shopSlug}
          shopName={detailData.summary.name}
          reviews={detailData.review.reviews}
          onClose={handleOverlayClose}
        />
      )}

      {/* Full Review Photos */}
      {overlayScreen === 'reviewPhotos' && (
        <FullReviewPhotoView
          shopName={detailData.summary.name}
          photoItems={detailData.review.reviewPhotoItems}
          onClose={handleOverlayClose}
          onPhotoClick={(images, index) => handlePhotoDetailOpen(images, index, '후기 사진')}
        />
      )}

      {/* Photo Detail Viewer */}
      {resolvedPhotoDetailData && (
        <PhotoDetailViewer
          images={resolvedPhotoDetailData.images}
          initialIndex={resolvedPhotoDetailData.initialIndex}
          title={resolvedPhotoDetailData.title}
          subtitle={resolvedPhotoDetailData.subtitle}
          onClose={handlePhotoDetailClose}
        />
      )}

      {lookbookPhotoDetailData && (
        <PhotoDetailViewer
          images={lookbookPhotoDetailData.images}
          initialIndex={lookbookPhotoDetailData.initialIndex}
          title={lookbookPhotoDetailData.title}
          subtitle={lookbookPhotoDetailData.subtitle}
          onClose={() => setLookbookPhotoDetailData(null)}
        />
      )}

      {/* Consultation Modal */}
      {showConsultation && (
        <ConsultationModal
          shop={detailData.consultationShop}
          onClose={() => setShowConsultation(false)}
        />
      )}

      {/* Hero */}
      <div className="md:pt-16">
        <VendorHero hero={detailData.hero} />

        <div className="max-w-[1120px] md:max-w-[960px] lg:max-w-[1080px] xl:max-w-[1180px] mx-auto">
          {/* ── Desktop layout (1024px+) ── */}
          <div className="hidden lg:flex lg:gap-7 xl:gap-10 lg:px-8 xl:px-10 pt-8 items-start">
            <div className="flex-1 min-w-0">
              {/* Vendor info */}
              {/* <span className="inline-block px-2.5 py-1 rounded-full bg-[#F5F3F0] text-[#7A736C] text-sm font-medium mb-4">
                후기 기반 검증 업체
              </span> */}
              <h1 className="text-[34px] font-bold text-[#211C18] leading-tight mb-2">{detailData.summary.name}</h1>
              <p className="text-sm text-[#7A736C] mb-5">
                {detailData.summary.location} &middot; 실제 후기 {detailData.summary.reviewCount}개 &middot; 후기 사진 {detailData.summary.photoCount}장
              </p>
              <p className="text-[16px] text-[#3F3A35] leading-[1.7] max-w-[720px] mb-5">{detailData.summary.description}</p>
              {/* <div className="flex flex-wrap gap-1.5 mb-2">
                {detailData.summary.tags.map((tag) => <AppChip key={tag} label={tag} />)}
              </div> */}
              {/* Tabs */}
              <AppTab tabs={TAB_OPTIONS} active={activeTab} onChange={(k) => setActiveTab(k as Tab)} />
              <div className="py-8">{hasActiveTabContent(activeTab)}</div>
            </div>
            <div className="w-[280px] xl:w-[320px] flex-shrink-0 sticky top-[100px] self-start">
              <DesktopSidebar sidebar={detailData.sidebar} onConsultationClick={() => setShowConsultation(true)} />
            </div>
          </div>

          {/* ── Tablet layout (768-1023px) ── */}
          <div className="hidden md:block lg:hidden px-10 pt-8">
            {/* <span className="inline-block px-2.5 py-1 rounded-full bg-[#F5F3F0] text-[#7A736C] text-sm font-medium mb-4">
              후기 기반 검증 업체
            </span> */}
            <h1 className="text-[34px] font-bold text-[#211C18] leading-tight mb-2">{detailData.summary.name}</h1>
            <p className="text-sm text-[#7A736C] mb-2">
              {detailData.summary.location} &middot; 실제 후기 {detailData.summary.reviewCount}개 &middot; 후기 사진 {detailData.summary.photoCount}장
            </p>
            <p className="text-[16px] text-[#3F3A35] leading-[1.7] mb-5">{detailData.summary.description}</p>
            {/* <div className="flex flex-wrap gap-1.5 mb-2">
              {detailData.summary.tags.map((tag) => <AppChip key={tag} label={tag} />)}
            </div> */}
            <AppTab tabs={TAB_OPTIONS} active={activeTab} onChange={(k) => setActiveTab(k as Tab)} />
            <div className="py-8">{hasActiveTabContent(activeTab)}</div>
          </div>

          {/* ── Mobile layout ── */}
          <div className="md:hidden">
            {/* Compact Header — fixed, slides in when scrolled past hero */}
            <div
              className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
                isCompactHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
              }`}
            >
              <div className="bg-static-white border-b border-[#E1DBD4] px-4 h-12 flex items-center" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center flex-shrink-0 cursor-pointer">
                  <i className="ri-arrow-left-line text-lg text-[#211C18]"></i>
                </button>
                <span className="flex-1 min-w-0 mx-1 text-[15px] font-semibold text-[#211C18] truncate">{detailData.summary.name}</span>
                {/* <button className="w-10 h-10 flex items-center justify-center flex-shrink-0 cursor-pointer">
                  <i className="ri-share-line text-lg text-[#211C18]"></i>
                </button> */}
              </div>
            </div>

            {/* Sentinel for scroll detection */}
            <div ref={sentinelRef} className="h-px -mt-px" />

            {/* <div className="px-5 pt-6" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              
              <div className="flex flex-wrap gap-1.5">
                {detailData.summary.tags.slice(0, 4).map((tag) => <AppChip key={tag} label={tag} />)}
              </div>
            </div> */}
            {/* Sticky Tab wrapper */}
            <div className="mt-0 bg-static-white sticky z-20" style={{ top: isCompactHeaderVisible ? '48px' : '0px' }}>
              <AppTab tabs={TAB_OPTIONS} active={activeTab} onChange={(k) => setActiveTab(k as Tab)} />
            </div>
            <div className="px-5 pt-6" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom, 0px))' }}>{hasActiveTabContent(activeTab)}</div>

            {/* Bottom CTA — hidden when sub-screen is active */}
            {!isSubScreen && (
              <>
                <BottomActionBar ctaLabel="상담 문의하기" onCtaClick={() => setShowConsultation(true)} />
                <div className="h-4"></div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
